import React, { useState } from 'react';
import styled from 'styled-components';
import AppButton from '../../shared/components/ui/AppButton';
import PageTemplate from '../../shared/components/layout/PageTemplate';

const logo = require('../../assets/logo-no-padding.png');

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 70vh;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    height: 75vh;
    padding: 0 8px;
  }
  
  @media (max-width: 375px) {
    height: 80vh;
    padding: 0 4px;
  }
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 20px;
  color: #333;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
  
  @media (max-width: 375px) {
    font-size: 16px;
    margin-bottom: 12px;
  }
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 10px;
  margin-bottom: 20px;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const Content = styled.div`
  line-height: 1.6;
  color: #555;
  
  p {
    margin-bottom: 12px;
  }
  
  ul {
    margin: 12px 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 8px;
    }
  }
  
  strong {
    color: #333;
    font-weight: 600;
  }
  
  @media (max-width: 375px) {
    font-size: 14px;
    line-height: 1.5;
    
    p {
      margin-bottom: 10px;
    }
    
    ul {
      margin: 10px 0;
      padding-left: 16px;
      
      li {
        margin-bottom: 6px;
      }
    }
  }
`;

const FixedBottomSection = styled.div`
  flex-shrink: 0;
  background-color: white;
  padding-top: 10px;
  border-top: 1px solid #e9ecef;
`;

const Header = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  
  p {
    margin-bottom: 8px;
    font-weight: 500;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Section = styled.section`
  margin-bottom: 24px;
  
  h4 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #333;
    border-bottom: 2px solid #e9ecef;
    padding-bottom: 6px;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
  
  label {
    cursor: pointer;
    font-weight: 500;
    color: #333;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    margin-top: 20px;
  }
  
  @media (max-width: 375px) {
    gap: 8px;
    margin-top: 16px;
  }
`;

// Types
export interface TermsSection {
  id: string;
  title: string;
  content: React.ReactNode;
}

interface TermsTemplateProps {
  title?: string;
  effectiveDate?: string;
  platformName?: string;
  contactEmail?: string;
  sections?: TermsSection[];
  onAccept: () => void;
  onBack?: () => void;
  onDecline?: () => void;
  isLoading?: boolean;
  requireCheckbox?: boolean;
  checkboxLabel?: string;
  buttonText?: string;
  logoSrc?: string;
  logoAlt?: string;
}

// Default sections
const defaultSections: TermsSection[] = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: (
      <p>
        By accessing or using the Illuminate website or mobile app (the "Service"), you agree to comply with and be legally bound by these Terms and our Privacy Policy. If you disagree with any part, do not use the Service.
      </p>
    )
  },
  {
    id: 'service-description',
    title: '2. Description of Service',
    content: (
      <>
        <p>
          Illuminate is a platform that connects students with vetted tutors for academic assistance. We provide tools for account creation, tutor discovery, communication, scheduling, and profile management.
        </p>
        <p>
          We do not process or handle payments. Financial transactions, if any, occur off-platform between students (or their guardians) and tutors.
        </p>
      </>
    )
  },
  {
    id: 'eligibility',
    title: '3. Eligibility',
    content: (
      <p>
        You must be at least <strong>13 years old</strong> to create an account. If under 18, you may only use the Service with <strong>parental or legal guardian consent</strong>. We may require parent/guardian contact information for account approval.
      </p>
    )
  },
  {
    id: 'required-info',
    title: '4. Required Information',
    content: (
      <>
        <p>
          To use the platform, users must provide <strong>truthful, accurate, and complete information</strong>, including:
        </p>
        <ul>
          <li>Full legal name</li>
          <li>Contact information (email, phone number)</li>
          <li>Home address</li>
          <li>Parent/guardian name and contact info (if a minor)</li>
          <li>Academic records (e.g., school name, transcript)</li>
        </ul>
        <p>
          Misrepresentation or submission of false documents is grounds for immediate termination.
        </p>
      </>
    )
  },
  {
    id: 'tutor-vetting',
    title: '5. Tutor Vetting',
    content: (
      <>
        <p>
          All tutors on Illuminate are subject to a <strong>vetting and verification process</strong> that may include:
        </p>
        <ul>
          <li>ID verification</li>
          <li>Academic background checks</li>
          <li>Interview or qualification review</li>
        </ul>
        <p>
          While we conduct these checks in good faith, we make <strong>no guarantees of teaching effectiveness</strong> and are not liable for outcomes resulting from any tutoring arrangement.
        </p>
      </>
    )
  },
  {
    id: 'parental-responsibility',
    title: '6. Parental Responsibility for Minors',
    content: (
      <>
        <p>For users under 18, a parent or legal guardian must:</p>
        <ul>
          <li>Provide verifiable consent</li>
          <li>Approve academic record submission</li>
          <li>Review tutor connections</li>
          <li>Supervise off-platform communication and payments</li>
        </ul>
        <p>
          Parents are <strong>solely responsible</strong> for decisions and agreements made by their minor.
        </p>
      </>
    )
  },
  {
    id: 'payments',
    title: '7. Payments and Financial Agreements',
    content: (
      <>
        <p>
          Illuminate does <strong>not process or facilitate</strong> payments of any kind. We:
        </p>
        <ul>
          <li>Do not monitor or track financial agreements</li>
          <li>Do not issue invoices or receipts</li>
          <li>Are not responsible for payment disputes, fraud, or refunds</li>
        </ul>
        <p>
          All payment arrangements must be handled directly between tutor and student or guardian.
        </p>
      </>
    )
  },
  {
    id: 'conduct',
    title: '8. Conduct Expectations',
    content: (
      <>
        <p>Users agree to act respectfully and responsibly. Specifically, users may not:</p>
        <ul>
          <li>Misrepresent credentials or identities</li>
          <li>Share fake or altered documents</li>
          <li>Harass, exploit, or harm others</li>
          <li>Use the Service to cheat or violate academic integrity</li>
          <li>Circumvent platform rules</li>
        </ul>
        <p>
          Tutors agree to maintain professionalism, confidentiality, and uphold ethical teaching standards.
        </p>
      </>
    )
  },
  {
    id: 'termination',
    title: '9. Termination',
    content: (
      <>
        <p>We reserve the right to suspend or permanently remove accounts for:</p>
        <ul>
          <li>Violating these Terms</li>
          <li>Submitting false information</li>
          <li>Using the platform for unauthorized purposes</li>
          <li>Failing to comply with community standards</li>
        </ul>
      </>
    )
  },
  {
    id: 'liability',
    title: '10. Limitation of Liability',
    content: (
      <>
        <p>Illuminate is <strong>not liable</strong> for:</p>
        <ul>
          <li>Payment or service disputes between users</li>
          <li>Academic performance results or tutoring effectiveness</li>
          <li>Off-platform interactions or agreements</li>
          <li>Any parental oversight failures</li>
          <li>Any breach of these Terms by a tutor or student</li>
        </ul>
      </>
    )
  },
  {
    id: 'indemnification',
    title: '11. Indemnification',
    content: (
      <p>
        You agree to indemnify and hold harmless Illuminate, its operators, and affiliates from any claims, losses, or damages arising from your use of the Service, including disputes with other users and off-platform issues.
      </p>
    )
  },
  {
    id: 'governing-law',
    title: '12. Governing Law',
    content: (
      <p>
        These Terms are governed by the laws of the United States of America. Any disputes shall be resolved exclusively in the courts of that jurisdiction.
      </p>
    )
  },
  {
    id: 'changes',
    title: '13. Changes to Terms',
    content: (
      <p>
        We may update these Terms. Material changes will be notified through email or platform updates. Continued use of the platform indicates acceptance.
      </p>
    )
  }
];

const TermsTemplate: React.FC<TermsTemplateProps> = ({
  title = "📄 TERMS AND CONDITIONS & PRIVACY POLICY",
  effectiveDate = "06/03/2025",
  platformName = "Illuminate",
  contactEmail = "illuminateusapp@gmail.com",
  sections = defaultSections,
  onAccept,
  onBack,
  onDecline,
  isLoading = false,
  requireCheckbox = false,
  checkboxLabel = "I have read and agree to the Terms and Conditions",
  buttonText = "Accept",
  logoSrc = logo,
  logoAlt = "Illuminate"
}) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  const canAccept = requireCheckbox ? isChecked : true;

  return (
    <PageTemplate logoSrc={logoSrc} logoAlt={logoAlt}>
      <Container>
        <Title>{title}</Title>
        
        <ScrollableContent>
          <Content>
            <Header>
              <p>Effective Date: {effectiveDate}</p>
              <p>Platform Name: {platformName}</p>
              <p>Contact Email: {contactEmail}</p>
            </Header>

            {sections.map((section) => (
              <Section key={section.id}>
                <h4>{section.title}</h4>
                {section.content}
              </Section>
            ))}
          </Content>
        </ScrollableContent>

        <FixedBottomSection>
          {requireCheckbox && (
            <CheckboxContainer>
              <input
                type="checkbox"
                id="terms-checkbox"
                checked={isChecked}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="terms-checkbox">{checkboxLabel}</label>
            </CheckboxContainer>
          )}

          <ButtonContainer>
            {onBack && (
              <AppButton
                title="Back"
                onClick={onBack}
                style={{ 
                  backgroundColor: '#6c757d',
                  flex: 1
                }}
              />
            )}
            {onDecline && (
              <AppButton
                title="Decline"
                onClick={onDecline}
                style={{ 
                  backgroundColor: '#dc3545',
                  flex: 1
                }}
              />
            )}
            <AppButton
              title={buttonText}
              onClick={onAccept}
              disabled={!canAccept || isLoading}
              style={{ 
                backgroundColor: '#9282CB',
                flex: 1
              }}
            />
          </ButtonContainer>
        </FixedBottomSection>
      </Container>
    </PageTemplate>
  );
};

export default TermsTemplate;
